// Best-effort JSON parser for CMS-supplied strings. The CMS Admin save flow
// does not enforce JSON validity, so fields like seoconfig.jsonLD or a
// component's config can ship malformed in several ways. A strict
// JSON.parse throws and, if unguarded, takes down the whole route's SSR
// render. (CON-8169)
//
// Recovery is layered, cheapest-first:
//   1. Try strict JSON.parse on the raw value.
//   2. Escape any unescaped control characters (LF/CR/TAB/etc.) that appear
//      inside string literals, and retry. Handles content where an author
//      pasted text containing literal newlines into a JSON string field.
//   3. If the value looks CSV-escaped (wrapped in extra outer quotes with
//      every inner `"` doubled to `""`, the way spreadsheets export quoted
//      cells), unwrap it and retry. Handles JSON-LD pasted from a CSV/
//      spreadsheet export — confirmed root cause of the WeHo SSR crash.
// If none of those parse, the caller's onError callback runs and the
// function returns `fallback`.

const escapeControlCharsInStringLiterals = raw => {
  let out = '';
  let inString = false;
  let escaped = false;

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];

    if (!inString) {
      if (ch === '"') inString = true;
      out += ch;
      continue;
    }

    if (escaped) {
      out += ch;
      escaped = false;
      continue;
    }

    if (ch === '\\') {
      out += ch;
      escaped = true;
      continue;
    }

    if (ch === '"') {
      out += ch;
      inString = false;
      continue;
    }

    const code = raw.charCodeAt(i);
    if (code < 0x20) {
      if (ch === '\n') out += '\\n';
      else if (ch === '\r') out += '\\r';
      else if (ch === '\t') out += '\\t';
      else if (ch === '\b') out += '\\b';
      else if (ch === '\f') out += '\\f';
      else out += `\\u${code.toString(16).padStart(4, '0')}`;
      continue;
    }

    out += ch;
  }

  return out;
};

// Detects values that look CSV-escaped: wrapped in outer `"..."` and with
// every inner `"` doubled to `""`. Conservative on purpose — any false
// positive would corrupt a legitimate JSON string. The opening "{ / "[
// requirement after unwrapping rules out values that just happen to start
// and end with quotes.
const looksCsvWrapped = raw =>
  raw.length >= 4 &&
  raw.charCodeAt(0) === 0x22 &&
  raw.charCodeAt(raw.length - 1) === 0x22 &&
  raw.includes('""') &&
  (raw[1] === '{' || raw[1] === '[');

const unwrapCsvEscapedJSON = raw => raw.slice(1, -1).replace(/""/g, '"');

const parseCmsJSON = (raw, { fallback = undefined, onError } = {}) => {
  if (raw == null || raw === '') return fallback;
  if (typeof raw !== 'string') return raw;

  try {
    return JSON.parse(raw);
  } catch (firstErr) {
    try {
      return JSON.parse(escapeControlCharsInStringLiterals(raw));
    } catch (_secondErr) {
      if (looksCsvWrapped(raw)) {
        try {
          const unwrapped = unwrapCsvEscapedJSON(raw);
          return JSON.parse(escapeControlCharsInStringLiterals(unwrapped));
        } catch (_thirdErr) {
          // fall through to fallback
        }
      }
      if (typeof onError === 'function') onError(firstErr);
      return fallback;
    }
  }
};

export default parseCmsJSON;
export {
  escapeControlCharsInStringLiterals,
  looksCsvWrapped,
  unwrapCsvEscapedJSON,
};
